import { useCallback, useEffect, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	MenuItem,
	OutlinedInput,
	Pagination,
	Select,
} from "@mui/material";
import {
	deleteMultipleSupportTicketAction,
	getSupportTicketAction,
} from "../../Actions/SupportTicketAction";
import { getPermissionData, getUserData } from "../../Actions/common";
import { MyCloseIcon, MyInfoOutlinedIcon } from "../../Assets/svg/MyIcons";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { API_IMAGE_URL, ITEMS_PER_PAGE_OPTIONS } from "../../config";
import { formatDateTime } from "../Common/DateFormat";
import CustomSnackbar from "../Common/snackBar";
import DialogOpenComponent from "./DialogOpenComponent";
import SupportTicketModel from "./EditSupportTicket";
import renderStatusBadge from "../../utils/badges";
import { CommonTooltip } from "../../utils/tooltip";
import DateRangePicker from "../Common/DateRangePicker";
import showConfirmation from "../../utils/Conformation";
import DataTable from "../../components/DataTable/DataTable";


const getDefaultDateRange = () => {
	const end = new Date();
	const start = new Date();
	start.setMonth(start.getMonth() - 1);
	return { from: start, to: end };
};



const SupportTicketSection = () => {
	const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(
		ITEMS_PER_PAGE_OPTIONS[0]
	);
	const [selectedRows, setSelectedRows] = useState([]);
	const [SupportTicketById, setSupportTicketById] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const isMenuOpen = Boolean(anchorEl);

	const [loading, setLoading] = useState(true);
	const [supportTicketData, setSupportTicketData] = useState([]);
	const [ticketId, setTicketId] = useState(0);
	const [parentComponent, setParentComponent] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [searchValue, setSearchValue] = useState("");
	const [enableFilterButton, setEnableFilterButton] = useState(false);
	const [totalRecords, setTotalRecords] = useState(0);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const [shouldFetchData, setShouldFetchData] = useState(1);
	const [openModal, setOpenModal] = useState(false);
	const [openStatusModal, setOpenStatusModal] = useState(false);
	const [selectedAttachment, setSelectedAttachment] = useState(null);
	const [statusFilter, setStatusFilter] = useState("all");

	const defaultRange = getDefaultDateRange();
	const [startDate, setStartDate] = useState(defaultRange.from.toISOString());
	const [endDate, setEndDate] = useState(defaultRange.to.toISOString());
	const [selectedRange, setSelectedRange] = useState(defaultRange);
	const [selectedDescription, setSelectedDescription] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const userData = localStorage.getItem("userData") ? getUserData() : null;
	const permissionData = localStorage.getItem(
		`${userData?.id}_permissionData`
	)
		? getPermissionData(`${userData?.id}_permissionData`)
		: [];
	const hasPermission = permissionData[0]?.role_permissions?.find(
		(m) => m?.module && m?.module?.slug === "support-ticket"
	);
	const [filterValues, setFilterValues] = useState({
		search: "",
		status: "all",
		dateRange: {
			from: defaultRange.from,
			to: defaultRange.to,
		},
	});

	const getSupportTicketData = useCallback(async () => {
		setLoading(true);
		await getSupportTicketAction(
			selectedRowsPerPage,
			currentPage,
			searchValue,
			startDate,
			endDate,
			statusFilter
		)
			.then((res) => {
				const { data } = res;
				if (data.status === 202) {
					setSnackbar({
						open: true,
						message: data?.message,
						severity: "info",
					});
				}
				if (data.status === 200) {
					setSupportTicketData(data?.data);
					setTotalRecords(data?.totalRecords || 0);
				}
			})
			.catch((error) => {
				setSnackbar({
					open: true,
					message: error?.response?.data?.message,
					severity: "error",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	}, [selectedRowsPerPage, currentPage, searchValue, startDate, endDate, statusFilter]);

	useEffect(() => {
		if (shouldFetchData) {
			getSupportTicketData();
			setShouldFetchData(0);
		}
	}, [shouldFetchData, getSupportTicketData]);

	useEffect(() => {
		const now = new Date();
		const oneMonthAgo = new Date();
		oneMonthAgo.setMonth(now.getMonth() - 1);

		setStartDate(oneMonthAgo.toISOString());
		setEndDate(now.toISOString());
		setSelectedRange({ from: oneMonthAgo, to: now });
	}, []);

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	const handleCloseModal = () => {
		setOpenModal(false);
		setSelectedAttachment(null);
	};

	const handleCloseStatusModal = () => {
		setOpenStatusModal(false);
	};

	const handleDelete = async (e, ticketId) => {
		e.stopPropagation();

		await showConfirmation("simpleDelete", {
			entityName: "Support Ticket",
			entityId: ticketId,
			action: deleteMultipleSupportTicketAction,
			onSuccess: (res) => {
				setSelectedRows([]);
				const remainingRecords = totalRecords - 1;
				const lastPage = Math.max(
					1,
					Math.ceil(remainingRecords / selectedRowsPerPage)
				);

				setCurrentPage((prev) => Math.min(prev, lastPage));
				setShouldFetchData(1);
				setCurrentPage(1);
				getSupportTicketData();
			},
			onError: (error) => {
				setSnackbar({
					open: true,
					message: error?.response?.data?.message || "Something went wrong",
					severity: "error",
				});
			},
			showCancelledMsg: true,
		});
	};


	const handleDeleteSelected = async (ids = selectedRows) => {
		const result = await showConfirmation("simpleMultipleDelete", {
			entityName: "Support Ticket",
			entityIds: ids,
			action: deleteMultipleSupportTicketAction,
			onSuccess: (res) => {
				setSelectedRows([]);
				setSelectedRows([]);

				const remainingRecords = totalRecords - ids.length;
				const lastPage = Math.max(
					1,
					Math.ceil(remainingRecords / selectedRowsPerPage)
				);

				setCurrentPage((prev) => Math.min(prev, lastPage));
				setShouldFetchData(1);
				setCurrentPage(1);
				getSupportTicketData();
			},
			onError: (error) => {
				setSnackbar({
					open: true,
					message:
						error?.response?.data?.message || "Something went wrong",
					severity: "error",
				});
				setSelectedRows([]);
			},
			showCancelledMsg: true,
		});

		if (result?.cancelled) {
			setSelectedRows([]);
		}
	};

	const handleDialogOpen = (data, id, parentComponentDetails) => {
		setTicketId(id);
		setSelectedDescription(data);
		setParentComponent(parentComponentDetails);
		setDialogOpen(true);
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
	};

	const columns = [
		{
			field: "ticketCode",
			headerName: "Ticket No.",
			renderCell: ({ row }) => <>#{row.ticketCode}</>,
		},
		{
			field: "domainUrl",
			headerName: "Tenant",
			renderCell: ({ row }) => (
				<span title={row.domainUrl}>{row.domainUrl}</span>
			),
		},
		{
			field: "moduleName",
			headerName: "Module",
			renderCell: ({ row }) =>
				row.moduleName ? (
					<span title={row.moduleName}>{row.moduleName}</span>
				) : (
					"N/A"
				),
		},
		{
			field: "description",
			headerName: "Description",
			align: "center",
			renderCell: ({ row }) =>
				row.description ? (
					<span
						onClick={() =>
							handleDialogOpen(
								row.description,
								row.id,
								"description"
							)
						}
						style={{ cursor: "pointer" }}
					>
						<CommonTooltip title="Description" arrow>
							<IconButton>
								<MyInfoOutlinedIcon />
							</IconButton>
						</CommonTooltip>
					</span>
				) : (
					"-"
				),
		},
		{
			field: "status",
			headerName: "Status",
			renderCell: ({ row }) => {
				const statusMap = {
					0: { text: "Pending", status: "warning" },
					1: { text: "In Progress", status: "info" },
					2: { text: "Failed", status: "error" },
					3: { text: "Completed", status: "success" },
					4: { text: "On Hold", status: "inactive" },
				};

				const { text, status } = statusMap[row.status] || {
					text: "Unknown",
					status: "default",
				};

				return renderStatusBadge({ status, title: text });
			},
		},
		{
			field: "createdAt",
			headerName: "Request Date",
			renderCell: ({ row }) => {
				const formattedDate = formatDateTime(row.createdAt, true);
				return <span title={formattedDate}>{formattedDate}</span>;
			},
		},
	];

	const filters = [
		{
			type: "search",
			key: "search",
			name: "search",
			placeholder: "Search ticket..."
		},
		{
			type: "select",
			key: "status",
			name: "status",
			label: "Status",
			options: [
				{ label: "All", value: "all" },
				{ label: "Pending", value: "0" },
				{ label: "In Progress", value: "1" },
				{ label: "Rejected", value: "2" },
				{ label: "Completed", value: "3" },
				{ label: "On Hold", value: "4" },
			]
		},
		{
			type: "dateRange",
			key: "dateRange",
			name: "dateRange",
			label: "Date Range"
		}
	];

	const handleEditClick = (rowData) => {
		setSupportTicketById(rowData);
		setOpenStatusModal(true);
	};


	const handleExportClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleFilterApply = (values) => {
		setFilterValues(values);
		setSearchValue(values.search || "");
		setStatusFilter(values.status || "all");
		if (values.dateRange?.from) {
			setStartDate(values.dateRange.from.toISOString());
		}
		if (values.dateRange?.to) {
			setEndDate(values.dateRange.to.toISOString());
		}
		setCurrentPage(1);
		setShouldFetchData(1);
	};

	const handleFilterReset = () => {
		const defaultRange = getDefaultDateRange();
		const resetValues = {
			search: "",
			status: "all",
			dateRange: {
				from: defaultRange.from,
				to: defaultRange.to,
			},
		};
		setFilterValues(resetValues);
		setSearchValue("");
		setStatusFilter("all");
		setStartDate(defaultRange.from.toISOString());
		setEndDate(defaultRange.to.toISOString());
		setCurrentPage(1);
		setShouldFetchData(1);
	};

	const actions = {
		view: hasPermission?.canView,
		delete: hasPermission?.canDelete,
	};

	const handleTableAction = (action, row) => {
		switch (action) {
			case "view":
				handleEditClick(row);
				break;

			case "delete":
				handleDelete(
					{ stopPropagation: () => { } },
					row.id
				);
				break;

			default:
				break;
		}
	};

	const bulkActions = {
		delete: hasPermission?.canDelete,
	};

	const handleBulkAction = (action, ids) => {
		if (action === "delete") {
			setSelectedRows(ids);
			handleDeleteSelected(ids);
		}
	};

	return (
		<div>
			<BreadcrumbsComponent />
			<DataTable

				columns={columns}
				rows={supportTicketData}
				loading={loading}
				totalCount={totalRecords}
				page={currentPage}
				pageSize={selectedRowsPerPage}
				filters={filters}
				filterValues={filterValues}
				actions={actions}
				bulkActions={bulkActions}
				getRowId={(row) => row.id}
				onAction={handleTableAction}

				onBulkAction={handleBulkAction}
				onFilterApply={handleFilterApply}
				onFilterReset={handleFilterReset}
				onPageChange={(page) => {
					setCurrentPage(page);
					setShouldFetchData(1);
				}}
				onPageSizeChange={(size) => {
					setSelectedRowsPerPage(size);
					setCurrentPage(1);
					setShouldFetchData(1);
				}}
			/>
			<Dialog
				open={openModal}
				onClose={handleCloseModal}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					Issues Image Preview
					<IconButton
						aria-label="close"
						onClick={handleCloseModal}
						sx={{ position: "absolute", right: 8, top: 8 }}
					>
						<MyCloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					{selectedAttachment ? (
						selectedAttachment.endsWith(".pdf") ? (
							<iframe
								src={`${API_IMAGE_URL}/${selectedAttachment}`}
								width="100%"
								height="500px"
								title="attachment"
							></iframe>
						) : (
							<img
								src={`${API_IMAGE_URL}/${selectedAttachment}`}
								alt="Attachment Preview"
								style={{ width: "100%", height: "auto" }}
							/>
						)
					) : (
						"No Attachment Available"
					)}
				</DialogContent>
			</Dialog>

			{dialogOpen && (
				<DialogOpenComponent
					ticketId={ticketId}
					data={selectedDescription}
					onClose={handleDialogClose}
					parentComponent={parentComponent}
				/>
			)}

			{openStatusModal && (
				<SupportTicketModel
					open={openStatusModal}
					handleClose={handleCloseStatusModal}
					ticketData={SupportTicketById}
					refetchSupportTickets={getSupportTicketData}
					setSnackbar={setSnackbar}
					permission={hasPermission}
				/>
			)}

			<CustomSnackbar
				open={snackbar.open}
				message={snackbar.message}
				severity={snackbar.severity}
				onClose={handleCloseSnackbar}
			/>
		</div>
	);
};

export default SupportTicketSection;
